/**
 * Which `/academy` URLs each deployment answers, and what to do with the rest.
 *
 * `/academy` is a STATIC page — no id, no CMS lookup — so the only thing a URL
 * can get wrong is the `/lang/<code>` segment, and the route
 * `academy/lang/:language` matches ANY segment. Left alone, every spelling was a
 * live, indexable 200 rendering the same page: `/academy/lang/es` on BR (a
 * language the deployment does not publish), `/academy/lang/pt-br` on BR (a
 * duplicate of the clean URL) and `/academy/lang/fibra-analytics` (a RELATIVE
 * router link followed from `/academy/lang/en` resolves as a sibling and lands
 * exactly there). Prerender.io cached each of them as a valid page.
 *
 * The valid set per deployment is the clean path plus the ONE non-default
 * supported language — precisely what `applyLangSuffix` emits for internal links:
 *
 *   BR     /academy (pt)  +  /academy/lang/en
 *   MX     /academy (es)  +  /academy/lang/en
 *   LATAM  /academy (en)  +  /academy/lang/es
 *   CO     — none. CO publishes no Academy (see `PRODUCT_CATALOG`), so every
 *           `/academy…` URL answers 404 there, the same way every
 *           `/products/<id>/<slug>` URL already does.
 *
 * Pure data + pure functions — no Angular, no `window` — mirroring
 * `product-catalog.ts`, so it is safe to import from route config and from SSR.
 */

import {
  findCatalogEntryByKind,
  normalizeProductSlug
} from "./product-catalog";

/** The one canonical, default-language Academy path. */
export const ACADEMY_PATH = "/academy";

/**
 * `/academy/lang/<segment>` spellings that move to `/academy` with a 301 instead
 * of answering 404, because the URL is already indexed and its correct target is
 * not in doubt. The products equivalent is `PRODUCT_REDIRECTS`: a redirect entry
 * exists ONLY where a real URL has one confident destination — everything else
 * is a 404, not a guess.
 *
 * Stored normalized (lowercase, no diacritics) — see `normalizeProductSlug`.
 */
export const ACADEMY_LANG_ALIASES: string[] = ["fibra-analytics"];

/**
 * What the Academy route should do with the requested URL.
 *
 * - `render`   — a published URL; keep it and canonicalize onto itself.
 * - `redirect` — a duplicate or a known alias; 301 to `ACADEMY_PATH`.
 * - `notFound` — this deployment publishes nothing at this URL; answer 404.
 */
export type AcademyRouteAction = "render" | "redirect" | "notFound";

export interface AcademyRouteRequest {
  /** `environment.platformId` — decides whether Academy is published at all. */
  platformId: string;
  /** Raw `:language` route param; `null` on the clean `/academy` route. */
  rawLangSegment: string | null;
  /**
   * The supported language that segment NAMES, or `undefined` when it names none.
   * Resolved by `SiilaTranslationService.getLanguageFromSuffixCode`, so the
   * accepted codes (including the legacy bare `pt`) and the per-deployment
   * supported set are read from their one owner rather than restated here.
   */
  segmentLanguage: string | undefined;
  /** `SiilaTranslationService.getDefaultLanguage()` — the suffixless language. */
  defaultLanguage: string;
}

/**
 * Classify one `/academy…` request. See {@link AcademyRouteAction}.
 *
 * The default language is deliberately a REDIRECT and not a 404: it is the same
 * page as `/academy` reached at a second URL, so consolidating it is right and
 * throwing it away is not. Only a segment that names no supported language and
 * is not a known alias has nothing to point at.
 */
export function resolveAcademyRoute(
  request: AcademyRouteRequest
): AcademyRouteAction {
  if (!findCatalogEntryByKind(request.platformId, "academy")) {
    return "notFound";
  }
  if (request.rawLangSegment == null) {
    return "render";
  }
  if (request.segmentLanguage) {
    return request.segmentLanguage === request.defaultLanguage
      ? "redirect"
      : "render";
  }
  const segment = normalizeProductSlug(request.rawLangSegment);
  if (!segment || ACADEMY_LANG_ALIASES.indexOf(segment) !== -1) {
    return "redirect";
  }
  return "notFound";
}
